/**
 * Analytics Service
 * Handles calculation of financial metrics and analytics for the Book of Accounts
 */
import { supabase as supabaseClient } from '@/lib/supabase-client';
const supabase = supabaseClient;
/**
 * Calculate Budget vs Actual analysis
 */
export async function calculateBudgetVsActual(organizationId, currency = 'USD', dateRange) {
    try {
        // Fetch all accounts with financial and categorization details
        const { data, error } = await supabase
            .from('accounts')
            .select(`
        id,
        name,
        account_financial_details(financial_status, next_payment_usd, next_payment_inr, total_balance_usd, total_balance_inr),
        account_categorizations(category, sub_category)
      `)
            .eq('organization_id', organizationId)
            .eq('is_active', true);
        if (error) {
            console.error('Error fetching budget data:', error);
            return [];
        }
        // Group by category and calculate totals
        const categoryMap = new Map();
        for (const account of data || []) {
            const category = account.account_categorizations?.[0]?.category || 'Uncategorized';
            const financial = account.account_financial_details?.[0];
            if (!categoryMap.has(category)) {
                categoryMap.set(category, { budgeted: 0, actual: 0 });
            }
            const values = categoryMap.get(category);
            // Get relevant amount based on currency
            if (currency === 'USD') {
                values.actual += financial?.total_balance_usd || 0;
                values.budgeted += financial?.next_payment_usd || 0;
            }
            else {
                values.actual += financial?.total_balance_inr || 0;
                values.budgeted += financial?.next_payment_inr || 0;
            }
        }
        // Convert to array and calculate variance
        const results = Array.from(categoryMap.entries()).map(([category, { budgeted, actual }]) => {
            const variance = actual - budgeted;
            const variancePercent = budgeted !== 0 ? (variance / budgeted) * 100 : 0;
            return {
                category,
                budgeted,
                actual,
                variance,
                variancePercent,
            };
        });
        return results.sort((a, b) => Math.abs(b.variance) - Math.abs(a.variance));
    }
    catch (error) {
        console.error('Error calculating budget vs actual:', error);
        return [];
    }
}
/**
 * Calculate Cash Flow for date range
 */
export async function calculateCashFlow(organizationId, months = 6) {
    try {
        // Fetch all accounts with financial details
        const { data, error } = await supabase
            .from('accounts')
            .select(`
        id,
        name,
        account_financial_details(
          next_payable_date,
          next_payment_usd,
          next_payment_inr,
          total_balance_usd,
          total_balance_inr
        )
      `)
            .eq('organization_id', organizationId)
            .eq('is_active', true);
        if (error) {
            console.error('Error fetching cash flow data:', error);
            return [];
        }
        // Generate date range
        const today = new Date();
        const cashFlowMap = new Map();
        // Initialize months
        for (let i = 0; i < months; i++) {
            const date = new Date(today);
            date.setMonth(date.getMonth() + i);
            const monthKey = date.toISOString().substring(0, 7); // YYYY-MM format
            cashFlowMap.set(monthKey, { inflow: 0, outflow: 0 });
        }
        // Categorize payments by month
        for (const account of data || []) {
            const financial = account.account_financial_details?.[0];
            if (!financial?.next_payable_date)
                continue;
            const paymentDate = new Date(financial.next_payable_date);
            const monthKey = paymentDate.toISOString().substring(0, 7);
            if (cashFlowMap.has(monthKey)) {
                const values = cashFlowMap.get(monthKey);
                // Assuming next_payment is outflow, total_balance is inflow
                values.outflow += financial.next_payment_usd || 0;
                values.inflow += financial.total_balance_usd || 0;
            }
        }
        // Convert to array and calculate running balance
        let runningBalance = 0;
        const results = Array.from(cashFlowMap.entries())
            .sort()
            .map(([month, { inflow, outflow }]) => {
            const netCashFlow = inflow - outflow;
            runningBalance += netCashFlow;
            return {
                month,
                inflow,
                outflow,
                netCashFlow,
                runningBalance,
            };
        });
        return results;
    }
    catch (error) {
        console.error('Error calculating cash flow:', error);
        return [];
    }
}
/**
 * Get upcoming payments in next N days
 */
export async function getUpcomingPayments(organizationId, days = 30) {
    try {
        const today = new Date();
        const futureDate = new Date(today);
        futureDate.setDate(futureDate.getDate() + days);
        const { data, error } = await supabase
            .from('accounts')
            .select(`
        id,
        name,
        account_financial_details(
          next_payable_date,
          next_payment_usd,
          next_payment_inr,
          total_balance_usd,
          total_balance_inr
        ),
        account_billing_schedules(priority, urgency)
      `)
            .eq('organization_id', organizationId)
            .eq('is_active', true);
        if (error) {
            console.error('Error fetching upcoming payments:', error);
            return [];
        }
        const upcoming = [];
        for (const account of data || []) {
            const financial = account.account_financial_details?.[0];
            const billing = account.account_billing_schedules?.[0];
            if (!financial?.next_payable_date)
                continue;
            const paymentDate = new Date(financial.next_payable_date);
            // Check if payment is within the date range
            if (paymentDate >= today && paymentDate <= futureDate) {
                upcoming.push({
                    accountId: account.id,
                    accountName: account.name,
                    dueDate: financial.next_payable_date,
                    amount: financial.next_payment_usd || financial.next_payment_inr || 0,
                    currency: financial.next_payment_usd ? 'USD' : 'INR',
                    priority: billing?.priority || 'bare_minimum',
                    urgency: billing?.urgency || 'unnecessary',
                });
            }
        }
        return upcoming.sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime());
    }
    catch (error) {
        console.error('Error fetching upcoming payments:', error);
        return [];
    }
}
/**
 * Get distribution of financial statuses
 */
export async function getFinancialStatusDistribution(organizationId) {
    try {
        const { data, error } = await supabase
            .from('account_financial_details')
            .select('financial_status')
            .eq('organization_id', organizationId);
        if (error) {
            console.error('Error fetching status distribution:', error);
            return [];
        }
        const statusMap = new Map();
        for (const record of data || []) {
            const status = record.financial_status || 'unknown';
            statusMap.set(status, (statusMap.get(status) || 0) + 1);
        }
        const total = data?.length || 1;
        const results = Array.from(statusMap.entries()).map(([status, count]) => ({
            status,
            count,
            percentage: Math.round((count / total) * 100),
        }));
        return results.sort((a, b) => b.count - a.count);
    }
    catch (error) {
        console.error('Error calculating status distribution:', error);
        return [];
    }
}
/**
 * Get variance analysis grouped by category or account
 */
export async function getVarianceAnalysis(organizationId, groupBy = 'category', currency = 'USD') {
    try {
        const { data, error } = await supabase
            .from('accounts')
            .select(`
        id,
        name,
        account_financial_details(
          next_payment_usd,
          next_payment_inr,
          total_balance_usd,
          total_balance_inr
        ),
        account_categorizations(category)
      `)
            .eq('organization_id', organizationId)
            .eq('is_active', true);
        if (error) {
            console.error('Error fetching variance data:', error);
            return [];
        }
        const groupMap = new Map();
        for (const account of data || []) {
            const groupKey = groupBy === 'category'
                ? account.account_categorizations?.[0]?.category || 'Uncategorized'
                : account.name;
            const financial = account.account_financial_details?.[0];
            if (!groupMap.has(groupKey)) {
                groupMap.set(groupKey, { budgeted: 0, actual: 0, accountCount: 0 });
            }
            const values = groupMap.get(groupKey);
            values.accountCount++;
            if (currency === 'USD') {
                values.budgeted += financial?.next_payment_usd || 0;
                values.actual += financial?.total_balance_usd || 0;
            }
            else {
                values.budgeted += financial?.next_payment_inr || 0;
                values.actual += financial?.total_balance_inr || 0;
            }
        }
        const results = Array.from(groupMap.entries()).map(([group, { budgeted, actual, accountCount }]) => ({
            category: group,
            budgeted,
            actual,
            variance: actual - budgeted,
            accountCount,
        }));
        return results.sort((a, b) => Math.abs(b.variance) - Math.abs(a.variance));
    }
    catch (error) {
        console.error('Error calculating variance analysis:', error);
        return [];
    }
}
/**
 * Get comprehensive analytics summary
 */
export async function getAnalyticsSummary(organizationId) {
    try {
        // Fetch all financial details
        const { data: financialData, error: financialError } = await supabase
            .from('account_financial_details')
            .select('total_balance_usd, next_payment_usd')
            .eq('organization_id', organizationId);
        if (financialError) {
            console.error('Error fetching financial data:', financialError);
        }
        // Fetch billing schedules for urgent/critical counts
        const { data: billingData, error: billingError } = await supabase
            .from('account_billing_schedules')
            .select('urgency, priority')
            .eq('organization_id', organizationId);
        if (billingError) {
            console.error('Error fetching billing data:', billingError);
        }
        // Calculate totals
        let totalBudgeted = 0;
        let totalActual = 0;
        for (const record of financialData || []) {
            totalBudgeted += record.next_payment_usd || 0;
            totalActual += record.total_balance_usd || 0;
        }
        // Count urgent/critical accounts
        let criticalAccounts = 0;
        let urgentAccounts = 0;
        for (const record of billingData || []) {
            if (record.urgency === 'very_urgent') {
                criticalAccounts++;
            }
            else if (record.urgency === 'urgent' || record.urgency === 'somewhat_urgent') {
                urgentAccounts++;
            }
        }
        const totalVariance = totalActual - totalBudgeted;
        const variancePercent = totalBudgeted !== 0 ? (totalVariance / totalBudgeted) * 100 : 0;
        return {
            totalBudgeted,
            totalActual,
            totalVariance,
            variancePercent,
            accountsTracked: financialData?.length || 0,
            criticalAccounts,
            urgentAccounts,
        };
    }
    catch (error) {
        console.error('Error calculating summary:', error);
        return {
            totalBudgeted: 0,
            totalActual: 0,
            totalVariance: 0,
            variancePercent: 0,
            accountsTracked: 0,
            criticalAccounts: 0,
            urgentAccounts: 0,
        };
    }
}
